import { Box, Collapse, Stack, Typography, useMediaQuery } from "@mui/material";
import { CaretDown } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { NavRoute } from "../../routes/navRoutes";
import { useCampaign } from "../../views/Campaign/CampaignContext";
import { isAdmin } from "../../utils/helpers";
import { useAuth } from "../../providers/AuthProvider";

interface SidenavItemProps {
  route: NavRoute;
  active: boolean;
  handleToggle?: () => void;
  isSubMenuOpen?: boolean;
  onSubMenuToggle?: () => void;
  onComingSoon?: (modal: string) => void;
}

export default function SidenavItem({ route, active, handleToggle, isSubMenuOpen, onSubMenuToggle, onComingSoon }: SidenavItemProps) {
  const { t } = useTranslation();
  const campaign = useCampaign();
  const { user } = useAuth();
  const subMenuOpen = isSubMenuOpen !== undefined ? isSubMenuOpen : active;

  const isMobile = useMediaQuery("(max-width:600px)");
  const hasSubRoutes = route.subRoutes && route.subRoutes.length > 0;

  // Vérifie si un sous-item est actuellement sélectionné
  const hasActiveSubItem = hasSubRoutes && route.subRoutes?.some(
    (subRoute) => window.location.pathname === subRoute.path
  );

  const handleClick = (e: React.MouseEvent) => {
    if (route.comingSoonModal) {
      e.preventDefault();
      onComingSoon?.(route.comingSoonModal);
      return;
    }
    if (hasSubRoutes && !route.hideChevron) {
      e.preventDefault();
      if (onSubMenuToggle) {
        onSubMenuToggle();
      }
    } else if (handleToggle) {
      handleToggle();
    }
  };

  // Détermine le style du titre du menu
  // Pour les menus avec chevron: actif si ouvert
  // Pour les menus sans chevron (hideChevron): actif seulement si sur la page ou sous-item actif
  const isMenuActive = active || hasActiveSubItem;
  const showBgOnTitle = isMenuActive && !hasActiveSubItem;

  return (
    <Stack className={isMobile ? "mobile" : ""}>
      <Stack
        component={hasSubRoutes && !route.hideChevron ? "div" : Link}
        to={hasSubRoutes && !route.hideChevron ? undefined : route.path}
        onClick={handleClick}
        direction="row"
        spacing={1.5}
        alignItems="center"
        className={isMenuActive ? "active" : ""}
        sx={{
          cursor: "pointer",
          bgcolor: showBgOnTitle ? "primary.light" : (subMenuOpen && hasSubRoutes && !isMenuActive ? "#F0F0F0" : "transparent"),
          borderRadius: "4px",
          p: "10px",
          height: "50px",
          boxSizing: "border-box",
          color: isMenuActive ? "primary.main" : (subMenuOpen && hasSubRoutes ? "#9E9E9E" : "caption"),
          textDecoration: "none",
          "&:hover": {
            bgcolor: isMenuActive ? "primary.light" : "#F0F0F0",
            color: isMenuActive ? "primary.main" : "#9E9E9E",
          },
        }}
      >
        <Box component={route.icon} weight={active ? "fill" : "regular"} size={20} sx={{ flexShrink: 0 }} />
        <Typography
          fontWeight={500}
          fontSize={16}
          sx={{
            flex: 1,
            whiteSpace: "nowrap",
          }}
          children={t(route.pathName)}
        />
        {route.isNew && (
          <Box
            sx={{
              bgcolor: "#3F5EFB",
              borderRadius: "6px",
              px: "8px",
              py: "2px",
              fontSize: "12px",
              fontWeight: 400,
              fontFamily: "Instrument Sans, sans-serif",
              color: "#fff",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {t("common.new", "New")}
          </Box>
        )}
        {route.comingSoonModal && (
          <Box
            sx={{
              bgcolor: "#DCFCE7",
              border: "1px solid #dcfce7",
              color: "#166534",
              borderRadius: "6px",
              px: "8px",
              lineHeight: "16px",
              py: "2px",
              fontSize: "14px",
              fontWeight: 500,
              fontFamily: "Instrument Sans, sans-serif",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {t("common.comingSoon", "Bientôt disponible")}
          </Box>
        )}
        {hasSubRoutes && !route.hideChevron && !route.comingSoonModal && (
          <CaretDown
            size={20}
            style={{
              flexShrink: 0,
              transform: subMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
        )}
      </Stack>

      {hasSubRoutes && route.subRoutes && (
        <Collapse in={subMenuOpen}>
          <Stack pt={0} pl={0}>
            {route.subRoutes.map((subRoute, index) => {
              if (subRoute?.hideAlways?.(campaign)) return null;
              if (!isAdmin(user) && subRoute?.hide?.(campaign)) return null;
              const subActive = window.location.pathname === subRoute.path;

              // Titre de section (non cliquable)
              if (subRoute.isSectionTitle) {
                return (
                  <Typography
                    key={index}
                    fontSize={16}
                    sx={{
                      color: "caption",
                      padding: "11px 14px",
                      fontWeight: 500,
                    }}
                    children={t(subRoute.pathName)}
                  />
                );
              }

              if (subRoute.comingSoonModal) {
                return (
                  <Box
                    key={index}
                    onClick={() => onComingSoon?.(subRoute.comingSoonModal!)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      borderRadius: "4px",
                      padding: "11px 14px",
                      pl: subRoute.indent ? "24px" : "14px",
                      "&:hover": {
                        bgcolor: "primary.light",
                        "& .coming-soon-label": { color: "primary.main" },
                      },
                    }}
                  >
                    <Typography
                      className="coming-soon-label"
                      fontSize={16}
                      fontWeight={500}
                      sx={{ color: "caption" }}
                    >
                      {t(subRoute.pathName)}
                    </Typography>
                    <Box
            sx={{
              bgcolor: "#DCFCE7",
              border: "1px solid #dcfce7",
              color: "#166534",
              borderRadius: "6px",
              px: "8px",
              lineHeight: "16px",
              py: "2px",
              fontSize: "14px",
              fontWeight: 500,
              fontFamily: "Instrument Sans, sans-serif",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
                    >
                      {t("common.comingSoon", "Bientôt disponible")}
                    </Box>
                  </Box>
                );
              }

              return (
                <Typography
                  key={index}
                  component={Link}
                  onClick={handleToggle}
                  to={subRoute.path}
                  className={subActive ? "active" : ""}
                  fontSize={16}
                  sx={{
                    color: subActive ? "primary.main" : "caption",
                    bgcolor: subActive ? "primary.light" : "transparent",
                    borderRadius: "4px",
                    textDecoration: "none",
                    cursor: "pointer",
                    padding: "11px 14px",
                    pl: subRoute.indent ? "24px" : "14px",
                    fontWeight: 500,
                    "&:hover": {
                      bgcolor: subActive ? "primary.light" : "#F0F0F0",
                      color: subActive ? "primary.main" : "#9E9E9E",
                    },
                  }}
                  children={t(subRoute.pathName)}
                />
              );
            })}
          </Stack>
        </Collapse>
      )}
    </Stack>
  );
}
